<template>
  <v-navigation-drawer
    :width="isDesktop ? 270 : 0"
    :app="!isDropdown"
    permanent
    :class="{ 'navigation--open': !isDesktop && isOpenNav }"
  >
    <div class="drawer d-flex flex-column fill-height">
      <div v-if="!isDropdown" class="logo-container">
        <Logo width="32px" height="120px" />
      </div>
      <div v-if="!isDropdown" class="mt-6 tw-px-3">
        <Button @click="onNewAutopilotProjectClick">
          <div class="tw-flex tw-gap-2 tw-items-center">
            <v-icon color="white">mdi-plus</v-icon>
            <span class="add-p-text">Add new project</span>
          </div>
        </Button>
      </div>
      <div class="tw-pl-3">
        <ProjectList
          v-if="!loading && status !== 'not_loaded' && status !== 'loading'"
          ref="listNav"
          :projectId="projectId"
          :isShowSessionSwitcher="isShowSessionSwitcher"
          @open="openProjectById"
          @getTop="$emit('getTop')"
          @isShowActions="$emit('isShowActions', $event)"
          @triggerProjectId="$emit('triggerProjectId', $event)"
        />
      </div>
    </div>
    <div v-if="isShowIntro" class="overlay"></div>
  </v-navigation-drawer>
</template>

<script>
import { mapState } from 'vuex';
import ProjectList from '@/components/widgets/ProjectListV2';
import Button from '@/components/v3/base/Button';

import Logo from './Logo';

export default {
  components: {
    Logo,
    ProjectList,
    Button,
  },

  props: {
    isDesktop: Boolean,
    isOpenNav: Boolean,
    currentProjectId: String,
    isDropdown: Boolean,
    isShowIntro: Boolean,
    isShowSessionSwitcher: Boolean,
  },

  data() {
    return {
      createProjectMenu: false,

      projectId: this.currentProjectId,
      loading: true,
    };
  },

  computed: {
    user() {
      return this.$store.getters['user/getUser'] || {};
    },

    isV2() {
      if (this.user && this.user.experiments) {
        return this.user.experiments.enable_v2_experience;
      }
      return false;
    },

    projects() {
      return this.$store.getters['projects/listProjects'];
    },

    isNotAllAutopilot() {
      return (
        this.projects &&
        this.projects.filter((proj) => !proj.in_auto_swipe_mode).length
      );
    },

    isSuperAdmin() {
      return this.$store.getters['user/getIsSuperAdmin'];
    },

    isSelfServe() {
      return this.$store.getters['user/getIsUserSelfServe'];
    },

    ...mapState({
      status: (state) => state.projects.status,
    }),
  },

  watch: {
    $route: 'updateRoute',

    currentProjectId(v) {
      this.projectId = v;
      const candidateId = this.$route.query.candidate || undefined;
      this.checkProjectInList(this.projectId, candidateId);
    },

    projects() {
      const candidateId = this.$route.query.candidate || undefined;
      this.checkProjectInList(this.projectId, candidateId);
    },
  },

  mounted() {
    this.updateRoute();
    window.addEventListener('click', this.onClickOutSide);
  },

  beforeDestroy() {
    window.removeEventListener('click', this.onClickOutSide);
  },

  methods: {
    scrollIntoView() {
      const el1 = this.$refs.listNav && this.$refs.listNav.$el;
      const el2 = this.$refs.projectList && this.$refs.projectList.$el;
      const el = el1 || el2;
      if (el) {
        setTimeout(() => {
          const element = el.querySelector('.selected-item');
          if (element) element.scrollIntoView();
        }, 100);
      }
    },

    async updateRoute() {
      this.loading = true;
      await this.$store.dispatch('projects/LOAD');
      this.loading = false;
      if (this.$route.query.project) {
        this.handleRoute();
      }
    },

    checkProjectInList(projectId, candidateId) {
      const result = this.projects.find(
        (item) => item.project_id === projectId
      );
      if (result) {
        this.$emit('open-project', { projectId, candidateId });
      }
    },

    handleRoute() {
      const projectId = this.$route.query.project;
      this.projectId = this.$route.query.project;
      if (this.$route.hash.includes('no-scroll')) {
        window.history.pushState('', document.title, window.location.pathname);
      } else {
        this.scrollIntoView();
      }
      const candidateId = this.$route.query.candidate || undefined;
      this.checkProjectInList(projectId, candidateId);
    },

    onClickOutSide(e) {
      if (!e.target.closest('.action-item')) {
        this.$emit('isShowActions', false);
      }
    },

    onNewAutopilotProjectClick() {
      if (this.isSelfServe) {
        this.$router.push('/autopilot-projects/self-serve/new');
        return;
      }
      this.$router.push('/autopilot-projects/new');
    },

    openProjectById(id) {
      this.projectId = id;
      if (
        this.$store.getters['projects/getProjectById'](id) &&
        this.$store.getters['projects/getProjectById'](id).status === '3'
      ) {
        this.$router.push(`/projects/v2/draft/${id}`);
        return;
      }
      this.$emit('open-project', { projectId: id });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/styles/variables.scss';

.navigation--open {
  width: 241px !important;
  box-shadow: 2px 0px 17px -6px rgba(90, 90, 90, 0.4);
}

.drawer {
  background-color: #fff;
}
.drawer__title {
  padding-bottom: 5px;
  font-size: 14px;
  text-transform: uppercase;
  line-height: normal;
  letter-spacing: 0.5px;
  color: #384955;
  font-weight: 500;
}

.projects {
  max-height: 290px;
  overflow-y: auto;
  overflow-x: visible;
}

.logo-container {
  height: 61px;
  padding: 15px 0 0 66px;
  background-color: #5656b2;
  z-index: 1;
  flex: 0 0 auto;
}

.element-space {
  padding: 0 20px;
}

.add-p-container {
  padding: 10px 14px;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.3), 0 1px 3px 0 rgba(0, 0, 0, 0.06);
  border-radius: 31px;
  width: 100%;
  .rounded-plus {
    background-color: $newAppBar;
    border-radius: 100%;
  }
  .add-p-text {
    margin-left: 8px;
    font-size: 14px;
    font-weight: 600;
    line-height: 24px;
    letter-spacing: 0.5px;
    color: $primary-dark;
  }
}
.add-p-container:hover {
  box-shadow: 0 1px 9px 0 rgba(0, 0, 0, 0.18), 0 1px 4px 0 rgba(0, 0, 0, 0.3),
    0 1px 3px 0 rgba(0, 0, 0, 0.06);
}

.left-nav-section-header {
  font-size: 14px;
  letter-spacing: 0.5px;
}

.list-item {
  padding: 5px 32px 5px 10px;
  &::before {
    visibility: hidden;
  }
  &.v-list-item--active {
    background-color: #f3f4f6;

    .v-list-item__title,
    .v-list-item__action {
      font-weight: 500;
      color: $black;
    }
  }
  &.customListItem {
    padding-right: 25px !important;
  }
  .v-list-item__title,
  .v-list-item__action {
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.5px;
    background-color: inherit;
    color: $gray-light;
    float: right !important;
  }
  .v-list-item__title {
    text-overflow: inherit !important;
    white-space: pre-wrap;
  }
}
.in-auto-swipe-mode {
  &::v-deep .v-list-item__icon {
    margin-right: 10px !important;
    min-width: 14px;
  }
  &::v-deep .v-icon {
    color: #0073bf;
    font-size: 18px;
  }
}
.calibration-toggle {
  position: absolute;
  top: 73%;
  &__text {
    font-weight: 500;
    font-size: 14px;
    letter-spacing: 0.5px;
    color: #384955;
  }
}
.action-item {
  position: absolute;
  top: 0px;
  right: 0px;
  padding: 14px 5px;
  height: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
  button {
    &:hover {
      background: #e6e7eb;
      border-radius: 100%;
    }
  }
}
.selected-item {
  background-color: #f3f4f6;
}
.new-project {
  &__title {
    font-weight: 400;
    text-transform: uppercase;
    font-size: 14px;
    line-height: 24px;
    letter-spacing: 0.2px;
    color: $gray-light;
    text-align: center;
  }
  &__box {
    padding: 16px 0 24px 0;
  }
  &__item {
    padding: 16px 31px 16px 31px;
    cursor: pointer;
    text-align: center;
  }
  &__item:hover {
    background-color: $title-color;
  }
  &__item-title {
    padding-bottom: 4px;
    font-weight: 500;
    font-size: 14px;
    letter-spacing: 0.2px;
    line-height: 24px;
    color: #111827;
  }
  &__item-text {
    font-size: 12px;
    letter-spacing: 0.17px;
    font-weight: 400;
    color: $gray-light;
  }
}
.new-project-menu {
  left: 0 !important;
}
.overlay {
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.15);
  z-index: 1;
}
.z-10 {
  z-index: 10;
}
</style>
